import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as StairsIcon } from "../assets/icons/user2/stairs.svg";
import { ReactComponent as ElevatorIcon } from "../assets/icons/user2/elevator.svg";

/**
 * RouteChoiceModal - Pop-up Modal displayed in center of screen
 * matching exact design layout from reference UI
 */
export default function RouteChoiceModal({
  isOpen,
  onClose,
  onSelectMode,
  selectedMode = "",
  destinationName = "",
}) {
  const { t } = useTranslation();
  const [tempMode, setTempMode] = useState(selectedMode || "elevator");

  useEffect(() => {
    if (isOpen) {
      setTempMode(selectedMode || "elevator");
    }
  }, [isOpen, selectedMode]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (tempMode) {
      onSelectMode(tempMode);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl border border-slate-100 flex flex-col items-center text-center animate-scale-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button X */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-400 hover:text-slate-700 flex items-center justify-center text-sm font-bold transition"
        >
          ✕
        </button>

        {/* Warning Badge Icon */}
        <div className="w-20 h-20 rounded-full border-4 border-amber-100 bg-amber-50 flex items-center justify-center text-amber-500 font-extrabold text-3xl mt-1 mb-3 shadow-inner">
          !
        </div>

        {/* Title */}
        <h3 className="text-xl font-extrabold text-slate-900 leading-snug">
          {t("user.layout.chooseRouteTitle", "เลือกวิธีการเดินทางระหว่างชั้น")}
        </h3>

        {/* Subtitle */}
        <p className="text-sm text-slate-500 mt-1.5 px-1 leading-relaxed">
          {destinationName
            ? `ปลายทาง "${destinationName}" อยู่คนละชั้นกับตำแหน่งของคุณ`
            : t("user.layout.chooseRouteSub", "ห้องปลายทางอยู่คนละชั้นกับตำแหน่งของคุณ กรุณาเลือกวิธีเดินทาง")}
        </p>

        {/* Selection Container */}
        <div className="w-full mt-4 bg-blue-50/70 border border-blue-100 rounded-2xl p-3 flex flex-col gap-2.5">
          {/* Elevator Option */}
          <button
            type="button"
            onClick={() => setTempMode("elevator")}
            className={`w-full p-3.5 rounded-xl border-2 transition-all flex items-center gap-3 text-left ${
              tempMode === "elevator"
                ? "border-blue-600 bg-white shadow-md text-blue-900 ring-2 ring-blue-500/20"
                : "border-transparent bg-white/70 hover:bg-white text-slate-700"
            }`}
          >
            <div
              className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                tempMode === "elevator" ? "bg-blue-600 text-white" : "bg-blue-100 text-blue-600"
              }`}
            >
              <ElevatorIcon className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-base flex items-center justify-between">
                <span>{t("user.layout.elevator", "ลิฟต์")}</span>
                {tempMode === "elevator" && (
                  <span className="text-[10px] bg-blue-600 text-white font-bold px-2 py-0.5 rounded-full">
                    เลือกแล้ว
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {t("user.layout.elevatorRecommend", "แนะนำสำหรับผู้ใช้รถเข็น หรือมีสัมภาระ")}
              </div>
            </div>
          </button>

          {/* Stairs Option */}
          <button
            type="button"
            onClick={() => setTempMode("stair")}
            className={`w-full p-3.5 rounded-xl border-2 transition-all flex items-center gap-3 text-left ${
              tempMode === "stair"
                ? "border-blue-600 bg-white shadow-md text-blue-900 ring-2 ring-blue-500/20"
                : "border-transparent bg-white/70 hover:bg-white text-slate-700"
            }`}
          >
            <div
              className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-colors ${
                tempMode === "stair" ? "bg-orange-500 text-white" : "bg-orange-100 text-orange-600"
              }`}
            >
              <StairsIcon className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-base flex items-center justify-between">
                <span>{t("user.layout.stairs", "บันได")}</span>
                {tempMode === "stair" && (
                  <span className="text-[10px] bg-blue-600 text-white font-bold px-2 py-0.5 rounded-full">
                    เลือกแล้ว
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {t("user.layout.stairsRecommend", "แนะนำสำหรับการเดินขึ้น-ลง 1-2 ชั้น")}
              </div>
            </div>
          </button>
        </div>

        {/* Action Buttons */}
        <div className="w-full mt-5 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={onClose}
            className="h-12 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 active:bg-slate-100 text-slate-700 font-bold text-base transition"
          >
            {t("user.layout.cancel", "ยกเลิก")}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!tempMode}
            className="h-12 rounded-xl bg-blue-600 hover:bg-blue-700 active:scale-[0.98] disabled:bg-slate-300 text-white font-bold text-base flex items-center justify-center gap-2 shadow-lg shadow-blue-500/25 transition"
          >
            <span>{t("user.menuList.startNavigation", "เริ่มนำทาง")}</span>
            <span className="text-lg">➔</span>
          </button>
        </div>
      </div>
    </div>
  );
}
